import { useState } from "react";

import { UserView } from "@/core/components/shared/preview-action";
import { Badge } from "@/core/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/core/components/ui/toggle-group";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";
import { ApprovalRequestStatusBadge } from "@/features/admin/components/approval-requests/approval-request-status-badge";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

type PayloadViewMode = "normal" | "json";

function parsePayload(raw: string): {
  parsed: unknown | null;
  pretty: string;
  isJson: boolean;
} {
  try {
    const parsed: unknown = JSON.parse(raw);
    return {
      parsed,
      pretty: JSON.stringify(parsed, null, 2),
      isJson: true,
    };
  } catch {
    return { parsed: null, pretty: raw, isJson: false };
  }
}

/**
 * Carte de synthèse d'une demande d'approbation (page détail) : type
 * d'action, service, ressource, motif, payload et horodatages.
 * Payload : vue structurée (`UserView`) par défaut, bascule JSON brut.
 * Identifiants et références en `NoTranslate`.
 */
export function ApprovalRequestDetailCard({
  approvalRequest,
}: {
  approvalRequest: ApprovalRequestDto;
}) {
  const { t } = useAdminTranslation();
  const [payloadView, setPayloadView] = useState<PayloadViewMode>("normal");
  const { parsed, pretty, isJson } = parsePayload(approvalRequest.payload);

  const fields: { label: string; value?: string | null }[] = [
    {
      label: t("admin.approvals.detail.requestedBy"),
      value: approvalRequest.requestedByKeycloakId,
    },
    {
      label: t("admin.approvals.detail.requestedAt"),
      value: formatDateWithFallback(approvalRequest.requestedAt, "—"),
    },
    {
      label: t("admin.approvals.detail.approvedBy"),
      value: approvalRequest.approvedByKeycloakId,
    },
    {
      label: t("admin.approvals.detail.approvedAt"),
      value: formatDateWithFallback(approvalRequest.approvedAt, "—"),
    },
    {
      label: t("admin.approvals.detail.rejectionReason"),
      value: approvalRequest.rejectionReason,
    },
    {
      label: t("admin.approvals.detail.appliedAt"),
      value: formatDateWithFallback(approvalRequest.appliedAt, "—"),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <NoTranslate as="span">
            <Badge variant="secondary">{approvalRequest.actionType}</Badge>
          </NoTranslate>
          <ApprovalRequestStatusBadge status={approvalRequest.status} />
          <NoTranslate as="span" className="font-mono text-xs text-muted-foreground">
            {approvalRequest.sourceService} · {approvalRequest.resourceType} ·{" "}
            {approvalRequest.resourceId}
          </NoTranslate>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm">
          <NoTranslate as="span">{approvalRequest.reason}</NoTranslate>
        </p>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {fields.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-sm text-muted-foreground">{label}</dt>
              <dd className="font-medium">
                <NoTranslate as="span">{value || "—"}</NoTranslate>
              </dd>
            </div>
          ))}
        </dl>
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {t("admin.approvals.detail.payload")}
            </p>
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              value={payloadView}
              onValueChange={(value) => {
                if (value === "normal" || value === "json") {
                  setPayloadView(value);
                }
              }}
              aria-label={t("admin.approvals.detail.payloadViewLabel")}
              className="justify-start"
            >
              <ToggleGroupItem value="normal" aria-label={t("admin.approvals.detail.payloadViewNormal")}>
                {t("admin.approvals.detail.payloadViewNormal")}
              </ToggleGroupItem>
              <ToggleGroupItem value="json" aria-label={t("admin.approvals.detail.payloadViewJson")}>
                {t("admin.approvals.detail.payloadViewJson")}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          {payloadView === "normal" && isJson && parsed !== null ? (
            <div className="rounded-md border border-border/70 bg-card p-3 sm:p-4">
              <UserView data={parsed} />
            </div>
          ) : (
            <pre
              translate="no"
              className="notranslate overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs"
            >
              {pretty}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
